import { useEffect, useState } from 'react'

import { apiService } from '@/service/api'
import { pnidPageEntriesFrom, type PnidPageEntry } from '@/utils/resolveGraphOpen'

/**
 * The two graphs an SLD page has (VISN-304).
 *
 * The ANNOTATION graph is the one SLD registration mints for each page: people
 * draw into it from scratch, and it is the ground truth. The PARSED graph is the
 * sld-parsing worker's output for the same page, kept apart under the parent's
 * `sld_parsed_graph_ids` so the parse never touches what people annotated. The
 * editor offers a switch between the two; the parsed graph is read-only.
 */
export type SldGraphView = 'annotation' | 'parsed'

export interface SldGraphViews {
  view: SldGraphView
  /** Null when the drawing has no annotation graph for this page. */
  annotationGraphId: string | null
  /** Null until a parse has landed for this page. */
  parsedGraphId: string | null
}

interface DocumentLike {
  type?: string
  properties?: Record<string, unknown> | null
}

function graphOnPage(entries: PnidPageEntry[], page: number): string | null {
  return entries.find((e) => e.page === page)?.graphId ?? null
}

/** Which of an SLD page's graphs `graphId` is, and the other one; null when it is neither. */
export function sldGraphViewsFor(
  doc: DocumentLike | null | undefined,
  graphId: string,
): SldGraphViews | null {
  if (!doc || doc.type !== 'sld') return null
  const props = doc.properties ?? {}
  const annotation = pnidPageEntriesFrom({ pnid_graph_ids: props.pnid_graph_ids })
  const parsed = pnidPageEntriesFrom({ pnid_graph_ids: props.sld_parsed_graph_ids })

  const annotated = annotation.find((e) => e.graphId === graphId)
  if (annotated) {
    return { view: 'annotation', annotationGraphId: graphId, parsedGraphId: graphOnPage(parsed, annotated.page) }
  }
  const parsedPage = parsed.find((e) => e.graphId === graphId)
  if (parsedPage) {
    return { view: 'parsed', annotationGraphId: graphOnPage(annotation, parsedPage.page), parsedGraphId: graphId }
  }
  return null
}

/**
 * The graph an SLD page opens on from the catalog: its parse when there is one,
 * since that is what people open a drawing to see, otherwise `graphId` (the
 * annotation graph, the only one until the parse lands).
 */
export function sldOpenGraphId(doc: DocumentLike | null | undefined, graphId: string): string {
  return sldGraphViewsFor(doc, graphId)?.parsedGraphId ?? graphId
}

/** How often to look again for a parse that has not landed yet, and for how long (ten minutes). */
export const PARSE_POLL_MS = 15_000
export const PARSE_POLL_MAX = 40

/**
 * The SLD views for the graph on screen, or null for any other graph.
 *
 * `enabled` is `sld_parsing_enabled`: with it off nothing is fetched, so the
 * editor behaves exactly as it did before SLDs had a parse. While the page's
 * parse has not landed, the parent is fetched again every `PARSE_POLL_MS`, so
 * the Parsed view becomes available without a reload.
 */
export function useSldGraphViews(
  documentId: string | null,
  graphId: string | undefined,
  enabled: boolean,
): SldGraphViews | null {
  const [views, setViews] = useState<SldGraphViews | null>(null)

  useEffect(() => {
    setViews(null)
    if (!enabled || !documentId || !graphId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let polls = 0

    const load = () => {
      apiService
        .getDocument(documentId)
        .then((doc) => {
          if (cancelled) return
          const next = sldGraphViewsFor(doc, graphId)
          setViews(next)
          polls += 1
          if (next?.view === 'annotation' && !next.parsedGraphId && polls < PARSE_POLL_MAX) {
            timer = setTimeout(load, PARSE_POLL_MS)
          }
        })
        .catch(() => {
          // A failed request is not an answer: keep what the switch shows and
          // look again, or a parse landing after one timeout never appears.
          if (cancelled) return
          polls += 1
          if (polls < PARSE_POLL_MAX) timer = setTimeout(load, PARSE_POLL_MS)
        })
    }
    load()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [documentId, graphId, enabled])

  return views
}
